import { useState, useMemo, useCallback } from "react";
import {
  AlertTriangle,
  CalendarDays,
  CalendarCheck2,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  Clock,
  Link2,
  Megaphone,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  Zap,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/layout/PageHeader";
import { config } from "@/lib/config";
import { cn } from "@/lib/utils";
import {
  useCalendarBlocks,
  useCalendarFeeds,
  useCreateBlock,
  useDeleteBlock,
  useCreateFeed,
  useRevokeFeed,
  useBroadcastOoo,
  useOutlookEvents,
  useSyncOutlookToFeed,
  type OutlookCalendarEvent,
  type CalendarBlock,
} from "@/hooks/useCalendar";

const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

function syncErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null && "response" in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (typeof data === "object" && data !== null) {
      const obj = data as { message?: string; title?: string; detail?: string };
      const msg = obj.message ?? obj.detail ?? obj.title;
      if (typeof msg === "string" && msg.trim()) return msg;
    }
  }
  return err instanceof Error ? err.message : "Could not sync meeting to feed.";
}

export function CalendarPage() {
  const blocks = useCalendarBlocks();
  const feeds = useCalendarFeeds();
  const createBlock = useCreateBlock();
  const deleteBlock = useDeleteBlock();
  const createFeed = useCreateFeed();
  const revokeFeed = useRevokeFeed();
  const broadcastOoo = useBroadcastOoo();
  const outlookEvents = useOutlookEvents();
  const syncOutlookToFeed = useSyncOutlookToFeed();

  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showOooBroadcast, setShowOooBroadcast] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);

  const feedBase = config.calendarFeedBase();
  const feedBaseMisconfigured = !isLocalDev && !feedBase;

  const activeFeeds = useMemo(
    () => (feeds.data ?? []).filter((f) => !f.revokedAt),
    [feeds.data],
  );

  const hasActiveFeed = activeFeeds.length > 0;

  const upcomingBlocks = useMemo(() => {
    const now = new Date().toISOString();
    return (blocks.data ?? []).filter((b) => b.endUtc > now);
  }, [blocks.data]);

  const pastBlocks = useMemo(() => {
    const now = new Date().toISOString();
    return (blocks.data ?? []).filter((b) => b.endUtc <= now).slice(-20);
  }, [blocks.data]);

  function feedUrl(token: string) {
    const base = feedBase;
    return base ? `${base}/calendar/feed/${token}.ics` : "";
  }

  function syncSuccessMessage() {
    const url = activeFeeds[0] ? feedUrl(activeFeeds[0].token) : null;
    return url
      ? `Saved to your feed. Subscribed calendars do not update instantly — they re-download the feed URL on their own schedule (often hours). Confirm the subscription uses ${url} (must be the caldera-api host, not caldera.firesoft.ai). Remove and re-add the subscription to see this block immediately.`
      : "Saved to blocked time. Generate a feed URL above so subscribers can see it.";
  }

  async function copyUrl(token: string, id: string) {
    await navigator.clipboard.writeText(feedUrl(token));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <>
      <PageHeader
        eyebrow="Calendar"
        title="Availability & ICS Feed"
        description="Create blocks in Caldera to sync to Outlook and your feed. Accepted Outlook meetings appear below, and you can promote them into the subscriber feed with one click."
      />

      {feedBaseMisconfigured && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            Feed URL is misconfigured (missing API base). Subscribers must use the API host
            (caldera-api.firesoft.ai), not the web app URL.
          </div>
        </div>
      )}

      {isLocalDev && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div>
            <strong>Local dev mode.</strong> Feed URLs point to localhost and won&apos;t work in
            Outlook, Google Calendar, or any external app. Deploy to Azure to get a public URL
            that calendar apps can subscribe to.
          </div>
        </div>
      )}

      {/* ── Feed token ──────────────────────────────────────────────── */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" />
            Subscription Feed URL
          </CardTitle>
          <CardDescription>
            {hasActiveFeed
              ? "Copy this API URL into calendar apps. Subscriptions must use caldera-api.firesoft.ai — not the Caldera web app URL."
              : "Generate a feed URL to share your availability."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {feeds.isLoading && <Skeleton className="h-12 w-full" />}

          {activeFeeds.map((feed) => (
            <div
              key={feed.id}
              className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{feed.feedName ?? "Feed"}</div>
                <div className="truncate text-xs text-muted-foreground font-mono">
                  {feedUrl(feed.token)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => copyUrl(feed.token, feed.id)}
                title="Copy URL"
              >
                {copiedId === feed.id ? (
                  <Check className="h-4 w-4 text-emerald-400" />
                ) : (
                  <ClipboardCopy className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (window.confirm("Delete this feed URL? Anyone subscribed will lose access.")) {
                    revokeFeed.mutate(feed.id);
                  }
                }}
                title="Delete feed URL"
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}

          {!feeds.isLoading && !hasActiveFeed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => createFeed.mutate("Caldera Availability")}
              disabled={createFeed.isPending}
            >
              <Plus className="h-3.5 w-3.5" />
              Generate Feed URL
            </Button>
          )}

          {hasActiveFeed && (
            <p className="text-xs text-muted-foreground">
              To regenerate, delete the current URL first. This revokes access for anyone currently subscribed.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── Outlook import card ──────────────────────────────────────── */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck2 className="h-4 w-4 text-primary" />
                From Outlook
              </CardTitle>
              <CardDescription>
                Accepted Outlook meetings that are not yet in your feed.
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => outlookEvents.refetch()}
              disabled={outlookEvents.isFetching}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", outlookEvents.isFetching && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {outlookEvents.isLoading && (
            <>
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </>
          )}

          {!outlookEvents.isLoading && outlookEvents.isError && (
            <p className="text-destructive">
              Could not load Outlook meetings. If this persists, the API may need Calendars.Read on the Graph app.
            </p>
          )}

          {!outlookEvents.isLoading && !outlookEvents.isError && (outlookEvents.data?.length ?? 0) === 0 && (
            <p className="text-muted-foreground">No upcoming Outlook meetings to sync right now.</p>
          )}

          {syncNotice && (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-100">{syncNotice}</p>
          )}

          {syncError && (
            <p className="text-destructive">{syncError}</p>
          )}

          {(outlookEvents.data ?? []).map((evt) => (
            <OutlookEventRow
              key={evt.graphEventId}
              event={evt}
              syncing={syncOutlookToFeed.isPending && syncOutlookToFeed.variables === evt.graphEventId}
              onSync={() => {
                setSyncError(null);
                setSyncNotice(null);
                syncOutlookToFeed.mutate(evt.graphEventId, {
                  onSuccess: () => {
                    setSyncError(null);
                    setSyncNotice(syncSuccessMessage());
                  },
                  onError: (err) => setSyncError(syncErrorMessage(err)),
                });
              }}
            />
          ))}
        </CardContent>
      </Card>

      {/* ── Upcoming blocks ─────────────────────────────────────────── */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-primary" />
                Blocked Time
              </CardTitle>
              <CardDescription>
                Your private view. Subscribers only see &quot;FirstName OOO&quot; from the feed URL above.
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowAddBlock(true)}>
              <Plus className="h-3.5 w-3.5" />
              Block Time
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {blocks.isLoading && <Skeleton className="h-20 w-full" />}

          {!blocks.isLoading && upcomingBlocks.length === 0 && (
            <p className="text-muted-foreground">No upcoming blocks. Add one above.</p>
          )}

          {upcomingBlocks.map((b) => (
            <BlockRow
              key={b.id}
              block={b}
              onDelete={() => deleteBlock.mutate(b.id)}
              onBroadcastOoo={() => setShowOooBroadcast(b.id)}
            />
          ))}

          {pastBlocks.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                Past blocks ({pastBlocks.length})
              </summary>
              <div className="mt-2 space-y-2 opacity-60">
                {pastBlocks.map((b) => (
                  <BlockRow key={b.id} block={b} onDelete={() => deleteBlock.mutate(b.id)} />
                ))}
              </div>
            </details>
          )}
        </CardContent>
      </Card>

      {/* ── How to subscribe card ───────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>How to subscribe</CardTitle>
          <CardDescription>
            Copy the feed URL above, then follow the steps for your calendar app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p><strong>Outlook (New / Web):</strong> Open Calendar view → click &quot;Add calendar&quot; in the left sidebar → &quot;Subscribe from web&quot; → paste URL</p>
          <p><strong>Google Calendar:</strong> Other Calendars (+) → From URL → paste URL</p>
          <p><strong>Apple Calendar:</strong> File → New Calendar Subscription → paste URL</p>
          <p className="pt-2 text-xs">
            Syncing adds blocks to the feed URL immediately, but subscribed calendar apps refresh on
            their own schedule (often 12–24 hours). Remove and re-add the subscription to force an update.
            The subscription URL must start with <span className="font-mono">https://caldera-api.firesoft.ai/calendar/feed/</span>.
          </p>
        </CardContent>
      </Card>

      {/* ── Add block dialog ────────────────────────────────────────── */}
      <AddBlockDialog
        open={showAddBlock}
        onClose={() => setShowAddBlock(false)}
        onSave={(input) => {
          createBlock.mutate(input, { onSuccess: () => setShowAddBlock(false) });
        }}
        saving={createBlock.isPending}
      />

      {/* ── OOO broadcast dialog ─────────────────────────────────────── */}
      <OooBroadcastDialog
        open={!!showOooBroadcast}
        blockId={showOooBroadcast}
        onClose={() => setShowOooBroadcast(null)}
        onSend={(emails) => {
          if (!showOooBroadcast) return;
          broadcastOoo.mutate(
            { blockId: showOooBroadcast, attendeeEmails: emails },
            { onSuccess: () => setShowOooBroadcast(null) },
          );
        }}
        sending={broadcastOoo.isPending}
      />
    </>
  );
}

function BlockRow({
  block,
  onDelete,
  onBroadcastOoo,
}: {
  block: CalendarBlock;
  onDelete: () => void;
  onBroadcastOoo?: () => void;
}) {
  const start = new Date(block.startUtc);
  const end = new Date(block.endUtc);

  const dayStr = start.toLocaleDateString("en-US", { weekday: "short" });
  const timeStr = block.isAllDay
    ? "All day"
    : `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;

  const durationMs = end.getTime() - start.getTime();
  const durationHrs = durationMs / 3_600_000;
  const durationLabel = block.isAllDay
    ? "Full day"
    : durationHrs < 1
      ? `${Math.round(durationHrs * 60)}m`
      : durationHrs % 1 === 0
        ? `${durationHrs}h`
        : `${durationHrs.toFixed(1)}h`;

  return (
    <div className="group flex items-stretch gap-3 rounded-lg border border-border/60 hover:border-border transition-colors overflow-hidden">
      {/* Date column */}
      <div className="flex flex-col items-center justify-center w-16 shrink-0 bg-muted/30 py-2">
        <span className="text-[10px] font-medium text-muted-foreground uppercase">{dayStr}</span>
        <span className="text-lg font-bold leading-tight">{start.getDate()}</span>
        <span className="text-[10px] text-muted-foreground">{start.toLocaleDateString("en-US", { month: "short" })}</span>
      </div>

      {/* Content */}
      <div className="flex-1 py-2 pr-2 flex items-center justify-between min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{block.label || block.publicSummary}</span>
            <Badge tone="neutral" className="text-[10px] shrink-0">{durationLabel}</Badge>
            {block.linkedToExistingOutlookEvent && (
              <Badge tone="neutral" className="text-[10px] shrink-0">From Outlook</Badge>
            )}
            {block.graphEventId && (
              <span title="Synced to Outlook">
                <CalendarCheck2 className="h-3.5 w-3.5 text-emerald-400" />
              </span>
            )}
            {block.oooBroadcastEventId && (
              <span title="OOO broadcast sent">
                <Megaphone className="h-3.5 w-3.5 text-blue-400" />
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
            <Clock className="h-3 w-3" />
            {timeStr}
          </div>
          {block.recurrenceRule && (
            <Badge tone="neutral" className="mt-1 text-[10px]">Recurring</Badge>
          )}
        </div>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {onBroadcastOoo && !block.oooBroadcastEventId && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onBroadcastOoo}
              title="Send OOO to team"
              className="h-8 w-8"
            >
              <Megaphone className="h-4 w-4 text-blue-400" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onDelete} title="Delete block" className="h-8 w-8">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function OutlookEventRow({
  event,
  syncing,
  onSync,
}: {
  event: OutlookCalendarEvent;
  syncing: boolean;
  onSync: () => void;
}) {
  const start = new Date(event.startUtc);
  const end = new Date(event.endUtc);
  const timeStr = event.isAllDay
    ? "All day"
    : `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} - ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;

  const dateStr = start.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-medium">{event.subject || "Busy"}</div>
        <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{dateStr} · {timeStr}</span>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Subscribers see a generic OOO block — not this title.
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={onSync} disabled={syncing}>
        {syncing ? "Syncing..." : "Sync to feed"}
      </Button>
    </div>
  );
}

function OooBroadcastDialog({
  open,
  blockId,
  onClose,
  onSend,
  sending,
}: {
  open: boolean;
  blockId: string | null;
  onClose: () => void;
  onSend: (emails: string[]) => void;
  sending: boolean;
}) {
  const [emails, setEmails] = useState("");

  function handleSend() {
    const parsed = emails
      .split(/[,;\n]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (parsed.length === 0) return;
    onSend(parsed);
  }

  if (!blockId) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Broadcast OOO"
      description="Send an Out-of-Office event to team members so they see you as unavailable."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSend} disabled={!emails.trim() || sending}>
            {sending ? "Sending…" : "Send OOO"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="ooo-emails">Team email addresses</Label>
          <Input
            id="ooo-emails"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="team@example.com, other@example.com"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Comma or newline separated. They&apos;ll receive a calendar invite showing you as OOO.
          </p>
        </div>
      </div>
    </Dialog>
  );
}

type QuickDuration = "30m" | "1h" | "2h" | "half" | "full" | "custom";

const QUICK_DURATIONS: { value: QuickDuration; label: string; icon?: typeof Clock }[] = [
  { value: "30m", label: "30 min" },
  { value: "1h", label: "1 hour" },
  { value: "2h", label: "2 hours" },
  { value: "half", label: "Half day" },
  { value: "full", label: "Full day" },
  { value: "custom", label: "Custom", icon: Settings2 },
];

const TIME_SLOTS = Array.from({ length: 28 }, (_, i) => {
  const totalMinutes = 7 * 60 + i * 30; // 7:00 AM to 8:30 PM
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const display = `${h > 12 ? h - 12 : h === 0 ? 12 : h}:${String(m).padStart(2, "0")} ${ampm}`;
  const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  return { display, value };
});

function getDaysGrid(baseDate: Date, weekOffset: number) {
  const start = new Date(baseDate);
  start.setDate(start.getDate() + weekOffset * 7);
  const dayOfWeek = start.getDay();
  start.setDate(start.getDate() - dayOfWeek);

  const days: Date[] = [];
  for (let i = 0; i < 14; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toIsoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function AddBlockDialog({
  open,
  onClose,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (input: Partial<CalendarBlock>) => void;
  saving: boolean;
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [duration, setDuration] = useState<QuickDuration>("1h");
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [label, setLabel] = useState("");
  const [weekOffset, setWeekOffset] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const isCustom = duration === "custom";
  const isFullDay = duration === "full";

  const days = useMemo(() => getDaysGrid(today, weekOffset), [today, weekOffset]);

  const weekLabel = useMemo(() => {
    const s = days[0];
    const e = days[13];
    const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(s)} – ${fmt(e)}`;
  }, [days]);

  const updateEndFromDuration = useCallback((dur: QuickDuration, start: string) => {
    if (dur === "custom" || dur === "full") return;
    const [h, m] = start.split(":").map(Number);
    let mins = h * 60 + m;
    if (dur === "30m") mins += 30;
    else if (dur === "1h") mins += 60;
    else if (dur === "2h") mins += 120;
    else if (dur === "half") mins += 240;
    const eh = Math.min(Math.floor(mins / 60), 23);
    const em = mins % 60;
    setEndTime(`${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`);
  }, []);

  function handleDurationChange(d: QuickDuration) {
    setDuration(d);
    if (d === "custom") {
      setShowAdvanced(true);
    } else {
      updateEndFromDuration(d, startTime);
    }
  }

  function handleStartTimeChange(t: string) {
    setStartTime(t);
    if (!isCustom) {
      updateEndFromDuration(duration, t);
    }
  }

  function handleSave() {
    const dateStr = toIsoDate(selectedDate);
    const startUtc = isFullDay
      ? new Date(`${dateStr}T00:00:00`).toISOString()
      : new Date(`${dateStr}T${startTime}:00`).toISOString();
    const endUtc = isFullDay
      ? new Date(`${dateStr}T23:59:59`).toISOString()
      : new Date(`${dateStr}T${endTime}:00`).toISOString();

    onSave({
      startUtc,
      endUtc,
      label: label || undefined,
      publicSummary: "Unavailable",
      isAllDay: isFullDay,
    });
  }

  const summaryTime = isFullDay
    ? "All day"
    : `${TIME_SLOTS.find((s) => s.value === startTime)?.display ?? startTime} – ${TIME_SLOTS.find((s) => s.value === endTime)?.display ?? endTime}`;

  const summaryDate = selectedDate.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Block Time"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            <Zap className="h-3.5 w-3.5" />
            {saving ? "Saving…" : "Block It"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Step 1: Duration presets */}
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</p>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {QUICK_DURATIONS.map((d) => (
              <button
                key={d.value}
                onClick={() => handleDurationChange(d.value)}
                className={cn(
                  "flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                  duration === d.value
                    ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/30"
                    : "border-border hover:border-primary/40 hover:bg-muted/40 text-foreground",
                )}
              >
                {d.icon && <d.icon className="h-3.5 w-3.5" />}
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: Day picker */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Day</p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setWeekOffset((w) => w - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground w-32 text-center">{weekLabel}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setWeekOffset((w) => w + 1)}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-center text-[10px] font-medium text-muted-foreground/60 uppercase">
                {d}
              </div>
            ))}
          </div>

          {/* Day grid - 2 rows of 7 */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day) => {
              const isPast = day < today;
              const isToday = isSameDay(day, today);
              const isSelected = isSameDay(day, selectedDate);
              return (
                <button
                  key={day.toISOString()}
                  disabled={isPast}
                  onClick={() => setSelectedDate(day)}
                  className={cn(
                    "relative flex flex-col items-center rounded-lg py-1.5 text-sm transition-all",
                    isPast && "opacity-30 cursor-not-allowed",
                    !isPast && !isSelected && "hover:bg-muted/60",
                    isSelected && "bg-primary text-primary-foreground shadow-md",
                    isToday && !isSelected && "ring-1 ring-primary/50",
                  )}
                >
                  <span className="text-[10px] leading-none text-inherit opacity-60">
                    {day.toLocaleDateString("en-US", { month: "short" })}
                  </span>
                  <span className="text-base font-semibold leading-tight">{day.getDate()}</span>
                  {isToday && (
                    <span className={cn(
                      "mt-0.5 h-1 w-1 rounded-full",
                      isSelected ? "bg-primary-foreground" : "bg-primary",
                    )} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 3: Time selection (not shown for Full Day) */}
        {!isFullDay && (
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {isCustom ? "Start & End Time" : "Start Time"}
            </p>
            <div className={cn("grid gap-3", isCustom ? "grid-cols-2" : "grid-cols-1")}>
              <Select
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
              >
                {TIME_SLOTS.map((s) => (
                  <option key={s.value} value={s.value}>{s.display}</option>
                ))}
              </Select>
              {isCustom && (
                <Select
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                >
                  {TIME_SLOTS.map((s) => (
                    <option key={s.value} value={s.value}>{s.display}</option>
                  ))}
                </Select>
              )}
            </div>
          </div>
        )}

        {/* Advanced / Label toggle */}
        {!showAdvanced ? (
          <button
            onClick={() => setShowAdvanced(true)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Settings2 className="h-3 w-3" />
            Add a label (private note)
          </button>
        ) : (
          <div>
            <Label htmlFor="block-label" className="text-xs text-muted-foreground">
              Label (private, not shared with subscribers)
            </Label>
            <Input
              id="block-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Client meeting, Focus time, PTO"
              className="mt-1"
            />
          </div>
        )}

        {/* Summary bar */}
        <div className="flex items-center gap-3 rounded-lg bg-muted/40 border border-border/60 px-4 py-3">
          <CalendarDays className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">{summaryDate}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> {summaryTime}
              {label && <span className="ml-2 truncate">· {label}</span>}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
